import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslate } from 'react-admin';
import {
  Box, Button, MenuItem, Skeleton, Stack, TableBody, TableCell, TableHead, TablePagination, TableRow,
  TableSortLabel, TextField, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import {
  DataTable, DatagridEmpty, FilterToggle, ListScreen, PageTitle, RecordCardList, SectionHeader, StatusDot,
} from '@/components/shell';
import type { StatEntry } from '@/features/tech-support/types';
import {
  errorRate, healthLabelKey, healthTone, periodCounts, useStatFormat, type StatPeriod,
} from './accountHealth';
import { fetchAllStatistics } from './fetchAllStatistics';

type SortField = 'accountName' | 'lastAccess' | 'ok' | 'err' | 'total' | 'rate';
type SortDir = 'asc' | 'desc';

const PERIODS: Array<{ id: StatPeriod; labelKey: string }> = [
  { id: 'day', labelKey: 'statistics.last_day' },
  { id: 'week', labelKey: 'statistics.last_week' },
  { id: 'month', labelKey: 'statistics.last_month' },
];

const PREFIX_CHOICES = [
  { id: '', labelKey: 'statistics.prefix_all' },
  { id: 'KR', labelKey: 'screens.statistics.prefixKiosk' },
  { id: 'OC', labelKey: 'screens.statistics.prefixOnline' },
];

/** The account's successful and failed requests in the period: both counts in words and the
 *  share of each as one bar. */
const PeriodCounts: React.FC<{ ok: number; err: number }> = ({ ok, err }) => {
  const t = useTranslate();
  const theme = useTheme();
  const fmt = useStatFormat();
  const total = ok + err;
  return (
    <Box sx={{ minWidth: 120 }}>
      <Typography variant="body2" component="div" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {t('screens.statistics.okErr', { ok: fmt.count(ok), err: fmt.count(err) })}
      </Typography>
      {total > 0 && (
        <Box aria-hidden sx={{ display: 'flex', height: 4, mt: 0.5, borderRadius: '2px', overflow: 'hidden' }}>
          <Box sx={{ width: `${(ok / total) * 100}%`, backgroundColor: theme.palette.primary.main }} />
          <Box sx={{ width: `${(err / total) * 100}%`, backgroundColor: theme.custom.status.problem.dot }} />
        </Box>
      )}
    </Box>
  );
};

const Health: React.FC<{ ok: number; err: number }> = ({ ok, err }) => {
  const t = useTranslate();
  const fmt = useStatFormat();
  const rate = errorRate(ok, err);
  return <StatusDot tone={healthTone(rate)} label={`${t(healthLabelKey(rate))} · ${fmt.rate(rate)}`} />;
};

const LastAccess: React.FC<{ value?: string }> = ({ value }) =>
  value ? <>{dayjs(value).format('DD/MM/YYYY HH:mm')}</> : <>—</>;

export const StatisticsList = () => {
  const t = useTranslate();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const fmt = useStatFormat();

  // Data
  const [rows, setRows] = useState<StatEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  // Scope and filters
  const [period, setPeriod] = useState<StatPeriod>('month');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [prefix, setPrefix] = useState('');

  // Sort
  const [sortField, setSortField] = useState<SortField>('accountName');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Every row of the last 31 days, read in pages of the server's maximum (500); sorting,
  // filtering and paging then happen here.
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await fetchAllStatistics());
      setFailed(false);
    } catch {
      setFailed(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Reset page when the scope, the filters or the order change
  useEffect(() => { setPage(0); }, [search, prefix, period, sortField, sortDir]);

  const processed = useMemo(() => {
    let data = [...rows];

    if (search) {
      const q = search.toLowerCase();
      data = data.filter(r => r.accountName?.toLowerCase().includes(q) || r.api?.toLowerCase().includes(q));
    }

    if (prefix) {
      data = data.filter(r => r.accountName?.startsWith(prefix));
    }

    data.sort((a, b) => {
      let cmp = 0;
      const fa = periodCounts(a, period), fb = periodCounts(b, period);
      switch (sortField) {
        case 'accountName': cmp = (a.accountName || '').localeCompare(b.accountName || ''); break;
        case 'lastAccess': cmp = (a.lastAccess || '').localeCompare(b.lastAccess || ''); break;
        case 'ok': cmp = fa.ok - fb.ok; break;
        case 'err': cmp = fa.err - fb.err; break;
        case 'total': cmp = (fa.ok + fa.err) - (fb.ok + fb.err); break;
        case 'rate': cmp = errorRate(fa.ok, fa.err) - errorRate(fb.ok, fb.err); break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return data;
  }, [rows, search, prefix, period, sortField, sortDir]);

  const paged = processed.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'accountName' ? 'asc' : 'desc');
    }
  };

  const periodLabel = t(PERIODS.find((p) => p.id === period)!.labelKey);
  const activeFilters = (search ? 1 : 0) + (prefix ? 1 : 0);

  const sortOptions: Array<{ id: SortField; label: string }> = [
    { id: 'accountName', label: t('statistics.account') },
    { id: 'lastAccess', label: t('statistics.last_access') },
    { id: 'ok', label: t('screens.statistics.successful') },
    { id: 'total', label: t('statistics.total') },
    { id: 'err', label: t('statistics.errors_col') },
    { id: 'rate', label: t('screens.statistics.errorRate') },
  ];

  const sortHeader = (field: SortField, label: string) => (
    <TableSortLabel
      active={sortField === field}
      direction={sortField === field ? sortDir : field === 'accountName' ? 'asc' : 'desc'}
      onClick={() => handleSort(field)}
    >
      {label}
    </TableSortLabel>
  );

  const empty = failed
    ? <DatagridEmpty title={t('screens.statistics.loadFailed')} hint={t('screens.statistics.loadFailedHint')} />
    : <DatagridEmpty title={t('statistics.no_results')} />;

  const pagination = (
    <TablePagination
      component="div"
      count={processed.length}
      page={page}
      onPageChange={(_, p) => setPage(p)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
      rowsPerPageOptions={[25, 50, 100]}
      labelRowsPerPage={t('statistics.rows_per_page')}
    />
  );

  const header = (
    <Box>
      <SectionHeader
        left={<PageTitle title={t('resources.statistics.name')} subtitle={t('screens.statistics.subtitle')} />}
        filters={<FilterToggle open={filtersOpen} onToggle={() => setFiltersOpen((o) => !o)} activeCount={activeFilters} />}
        onRefresh={fetchData}
        refreshing={loading}
      />

      {/* The period is the scope of every figure on the page, so it stays in view. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, pb: 1.5 }}>
        <Stack direction="row" spacing={1} role="group" aria-label={t('screens.statistics.period')} useFlexGap flexWrap="wrap">
          {PERIODS.map((p) => (
            <Button
              key={p.id}
              variant={period === p.id ? 'contained' : 'outlined'}
              aria-pressed={period === p.id}
              onClick={() => setPeriod(p.id)}
            >
              {t(p.labelKey)}
            </Button>
          ))}
        </Stack>
        {isPhone && (
          <TextField
            select
            size="small"
            label={t('screens.statistics.sortBy')}
            value={`${sortField}:${sortDir}`}
            onChange={(e) => {
              const [field, dir] = e.target.value.split(':') as [SortField, SortDir];
              setSortField(field);
              setSortDir(dir);
            }}
            fullWidth
          >
            {sortOptions.flatMap((o) => [
              <MenuItem key={`${o.id}:asc`} value={`${o.id}:asc`}>{t('screens.statistics.sortAsc', { field: o.label })}</MenuItem>,
              <MenuItem key={`${o.id}:desc`} value={`${o.id}:desc`}>{t('screens.statistics.sortDesc', { field: o.label })}</MenuItem>,
            ])}
          </TextField>
        )}
        {!loading && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: { sm: 'auto' } }} role="status">
            {t('screens.statistics.resultCount', { smart_count: processed.length, count: fmt.count(processed.length) })}
          </Typography>
        )}
      </Box>

      {filtersOpen && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, pb: 1.5 }}>
          <TextField
            size="small"
            label={t('ra.action.search')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ minWidth: 220, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
            type="search"
          />
          <TextField
            select
            size="small"
            value={prefix}
            onChange={e => setPrefix(e.target.value)}
            label={t('statistics.prefix')}
            sx={{ minWidth: 220, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
          >
            {PREFIX_CHOICES.map(c => (
              <MenuItem key={c.id} value={c.id}>{t(c.labelKey)}</MenuItem>
            ))}
          </TextField>
        </Box>
      )}
    </Box>
  );

  if (isPhone) {
    return (
      <ListScreen header={header}>
        {loading ? (
          <Stack spacing={1}>{[...Array(4)].map((_, i) => <Skeleton key={i} variant="rounded" height={96} />)}</Stack>
        ) : (
          <>
            <RecordCardList
              records={paged}
              getKey={(r) => `${r.accountName}-${r.api}`}
              empty={empty}
              mobile={{
                primary: (r) => r.accountName,
                status: (r) => {
                  const { ok, err } = periodCounts(r, period);
                  return <Health ok={ok} err={err} />;
                },
                lines: [
                  { label: t('screens.statistics.api'), render: (r) => r.api },
                  {
                    label: periodLabel,
                    render: (r) => {
                      const { ok, err } = periodCounts(r, period);
                      return t('screens.statistics.okErr', { ok: fmt.count(ok), err: fmt.count(err) });
                    },
                  },
                  { label: t('statistics.last_access'), render: (r) => <LastAccess value={r.lastAccess} /> },
                ],
              }}
            />
            {processed.length > 0 && pagination}
          </>
        )}
      </ListScreen>
    );
  }

  return (
    <ListScreen header={header}>
      <DataTable
        fill
        label={t('resources.statistics.name')}
        empty={!loading && paged.length === 0 ? empty : undefined}
        footer={processed.length > 0 ? pagination : undefined}
      >
        <TableHead>
          <TableRow>
            <TableCell>{sortHeader('accountName', t('statistics.account'))}</TableCell>
            <TableCell>{sortHeader('lastAccess', t('statistics.last_access'))}</TableCell>
            <TableCell sx={{ minWidth: 150 }}>{sortHeader('ok', t('screens.statistics.periodColumn', { period: periodLabel }))}</TableCell>
            <TableCell align="right">{sortHeader('total', t('statistics.total'))}</TableCell>
            <TableCell align="right">{sortHeader('err', t('statistics.errors_col'))}</TableCell>
            <TableCell>{sortHeader('rate', t('statistics.health'))}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading
            ? [...Array(8)].map((_, i) => (
              <TableRow key={i}>
                {[...Array(6)].map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}
              </TableRow>
            ))
            : paged.map((row) => {
              const { ok, err } = periodCounts(row, period);
              return (
                <TableRow key={`${row.accountName}-${row.api}`}>
                  <TableCell>
                    <Box>{row.accountName}</Box>
                    <Typography variant="caption" color="text.secondary">{row.api}</Typography>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}><LastAccess value={row.lastAccess} /></TableCell>
                  <TableCell><PeriodCounts ok={ok} err={err} /></TableCell>
                  <TableCell align="right">{fmt.count(ok + err)}</TableCell>
                  <TableCell align="right">{fmt.count(err)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}><Health ok={ok} err={err} /></TableCell>
                </TableRow>
              );
            })}
        </TableBody>
      </DataTable>
    </ListScreen>
  );
};
